// --- UPDATED FILE: packages/agent-kit/src/hooks/sprints/useSprintBoard.ts ---

import useSWR from 'swr';
import { sprintService, isApiError } from '@dopomogai/api-client';
import type { PublicSprintDashboardResponse, UUID } from '@dopomogai/types';
import { AgentKitError } from '../../types';


/**
 * The fetcher function that calls the API to get the full dashboard for a single sprint.
 * It now includes the locale.
 * @param key The SWR key array.
 */
const sprintBoardFetcher = async (
  [, sprintId, locale]: [string, UUID, string]
): Promise<PublicSprintDashboardResponse> => {
    const response = await sprintService.getSprintDashboard(sprintId, locale);
    if (isApiError(response)) {
        throw new AgentKitError(`Failed to fetch sprint dashboard for ${sprintId}`, response);
    }
    return response;
};

/**
 * A React hook to fetch all data required for a sprint's detail/management view.
 * It is conditionally fetched and includes the locale for multilingual support.
 *
 * @param sprintId The UUID of the sprint to fetch data for. If undefined, no fetch occurs.
 * @param locale The desired language for the translations, defaults to 'en'.
 */
export const useSprintBoard = (sprintId?: UUID, locale: string = 'en') => {
  // The SWR key now includes the locale to ensure a unique cache entry per language.
  const swrKey = sprintId ? ['/v1/sprint/dashboard', sprintId, locale] : null;
  
  const { data, error, isLoading, mutate } = useSWR<PublicSprintDashboardResponse, AgentKitError>(
    swrKey,
    sprintBoardFetcher,
    {
      revalidateOnFocus: false,
      keepPreviousData: true, // Keeps UI stable when switching between sprints
    }
  );

  return {
    // The full response object from the API
    sprintDashboardData: data,
    // Convenient shortcuts to the most commonly used parts of the data
    board: data?.board,
    kpiData: data?.kpi_data,
    translations: data?.translations, // Expose the language pack
    kpiTranslations: data?.kpi_translations, // Expose the KPI language pack
    error,
    isLoading,
    mutate,
  };
};